import { useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { bookingService } from '@/api/services/bookingService'
import { ChangeTrainerModal } from '@/features/bookings/ChangeTrainerModal'
import { EditSessionModal } from '@/features/bookings/EditSessionModal'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { cn, DUMMY_AVATAR, onAvatarError, localized, resolveName } from '@/shared/utils'
import { Modal } from '@/shared/ui/Modal'
import { Dirham } from '@/shared/ui/Dirham'
import {
  ChevronLeft, Edit2, Phone, Mail, Calendar, MapPin,
  Car, Settings, Users, BookOpen, CreditCard,
  CheckCircle, Clock, AlertCircle, Copy, Check,
  Package, ChevronDown, ReceiptText, Infinity as InfinityIcon, TrendingUp,
  Navigation,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(v?: string | null) {
  if (!v) return '—'
  try { return new Date(v).toLocaleDateString('en-GB').replace(/\//g, '-') } catch { return v }
}
function fmtDT(v?: string | null) {
  if (!v) return '—'
  try {
    return new Date(v).toLocaleString('en-GB', {
      day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit', hour12:true
    })
  } catch { return v }
}

function bookingStatusCls(s: string) {
  const u = s?.toUpperCase()
  if (u === 'PENDING')     return 'bg-brand-100 text-brand-700'
  if (u === 'CONFIRMED')   return 'bg-brand-100 text-brand-700'
  if (u === 'COMPLETED')   return 'bg-green-100 text-green-700'
  if (u === 'CANCELLED')   return 'bg-red-100 text-red-600'
  if (u?.includes('PROGRESS')) return 'bg-purple-100 text-purple-700'
  return 'bg-gray-100 text-gray-600'
}
const STATUS_KEY_MAP: Record<string, string> = {
  PENDING: 'pending', CONFIRMED: 'confirmed', COMPLETED: 'completed', CANCELLED: 'cancelled',
  IN_PROGRESS: 'inProgress', 'IN PROGRESS': 'inProgress', PAID: 'paid', FAILED: 'failed', REFUNDED: 'refunded',
}
function statusLabel(t: TFunction, raw?: string | null) {
  if (!raw) return raw
  const key = STATUS_KEY_MAP[raw.toUpperCase()]
  return key ? t(`status.${key}`, raw) : raw
}
function sessionStatusCls(s: string) {
  const u = s?.toUpperCase()
  if (u === 'COMPLETED')   return 'bg-green-500 text-white'
  if (u === 'CANCELLED')   return 'bg-red-500 text-white'
  if (u === 'CONFIRMED')   return 'bg-brand-500 text-white'
  if (u === 'PENDING')     return 'bg-brand-500 text-white'
  if (u?.includes('PROGRESS')) return 'bg-purple-500 text-white'
  return 'bg-gray-200 text-gray-700'
}

// ── Section header 
function SectionHeader({ label, color = 'amber' }: { label: string; color?: string }) {
  const cls = {
    amber: 'bg-brand-500', blue: 'bg-brand-500', green: 'bg-green-500', teal: 'bg-teal-500',
  }[color] ?? 'bg-brand-500'
  return (
    <div className={cn('px-4 py-2.5 flex items-center gap-2', cls)}>
      <div className="w-2 h-2 rounded-full bg-white"/>
      <span className="text-sm font-bold text-white">{label}</span>
    </div>
  )
}

// ── Info row ──────────────────────────────────────────────────────────────────
function InfoRow({ label, value, mono }: { label: string; value?: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 gap-4">
      <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
      <span className={cn('text-xs font-semibold text-gray-800 text-end', mono && 'font-mono')}>{value ?? '—'}</span>
    </div>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function Sk({ className }: { className?: string }) {
  return <div className={cn('animate-pulse bg-gray-200 rounded-lg', className)}/>
}

// ── Session info cell (icon + label + value) ──────────────────────────────────
function SessionInfoCell({ label, value, mono }: {
  label: string; value?: React.ReactNode; mono?: boolean
}) {
  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="text-[11px] text-gray-400 uppercase font-semibold tracking-wide mb-0.5">{label}</div>
      <div className={cn('text-sm font-bold text-gray-900', mono && 'font-mono')}>{value ?? '—'}</div>
    </div>
  )
}

// ── Section title bar (amber background, icon + label) ────────────────────────
function SessionSection({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-brand-50 border-b border-brand-100">
      <div className="w-8 h-8 rounded-full bg-white border border-brand-200 flex items-center justify-center flex-shrink-0 shadow-sm">
        {icon}
      </div>
      <span className="text-sm font-bold text-gray-800">{label}</span>
    </div>
  )
}

// ── Session Detail Modal ──────────────────────────────────────────────────────
function SessionDetailModal({ open, onClose, session, bookingId, onEdit }: {
  open: boolean; onClose: () => void; session: any | null; bookingId?: number; onEdit?: (s: any) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  // Booking tracking is a super-admin-only feature — it has no /school route.
  const isSchool = location.pathname.startsWith('/school')
  if (!session) return null

  // ── Build timeline from real API timestamp fields ─────────────────────────
  // The API does NOT return a timeline[] array — steps are derived from
  // individual timestamp fields: trainer_travel_started_at, trainer_reached_at, etc.
  const sStatus = (session.session_status ?? '').toUpperCase()

  const STEPS = [
    {
      key: 'confirmed',
      label: t('sessionDetail.confirmed'),
      desc: t('sessionDetail.confirmedDesc'),
      time: session.start_time ?? null,
      doneBy: t('sessionDetail.trainerRole'),
      isDone: ['COMPLETED','IN_PROGRESS','IN PROGRESS'].includes(sStatus) || !!session.start_time,
    },
    {
      key: 'on_my_way',
      label: t('sessionDetail.onMyWay'),
      desc: t('sessionDetail.onMyWayDesc'),
      time: session.trainer_travel_started_at ?? null,
      doneBy: t('sessionDetail.trainerRole'),
      isDone: !!session.trainer_travel_started_at,
    },
    {
      key: 'arrived',
      label: t('sessionDetail.arrived'),
      desc: t('sessionDetail.arrivedDesc'),
      time: session.trainer_reached_at ?? null,
      doneBy: t('sessionDetail.trainerRole'),
      isDone: !!session.trainer_reached_at,
    },
    {
      key: 'in_progress',
      label: t('sessionDetail.inProgress'),
      desc: t('sessionDetail.inProgressDesc'),
      time: session.start_time ?? null,
      doneBy: t('sessionDetail.trainerRole'),
      isDone: ['COMPLETED','IN_PROGRESS','IN PROGRESS'].includes(sStatus),
    },
    {
      key: 'completed',
      label: t('sessionDetail.completed'),
      desc: t('sessionDetail.completedDesc'),
      time: session.end_time ?? null,
      doneBy: t('sessionDetail.trainerRole'),
      isDone: sStatus === 'COMPLETED',
    },
  ]

  // Current = first non-done step (or last step if all done)
  const currentIdx = STEPS.findIndex(s => !s.isDone)
  const activeIdx  = currentIdx === -1 ? STEPS.length - 1 : currentIdx

  // Explicit timeline from API (old shape) — still supported if returned
  const hasExplicitTimeline = Array.isArray(session.timeline) && session.timeline.length > 0
  const explicitTimeline: any[] = session.timeline ?? []

  // Format session_no like "1st & 2nd"
  const ordinal = (n: number) => `${n}${['st','nd','rd'][n-1] ?? 'th'}`
  const sessionNoDisplay = Array.isArray(session.session_no)
    ? session.session_no.map(ordinal).join(' & ')
    : session.session_no != null
      ? ordinal(Number(session.session_no))
      : '—'

  return (
    <Modal open={open} onClose={onClose} title={t('sessionDetail.title')} size="md">
      <div className="space-y-0 divide-y divide-gray-100">

        {/* Edit the session, and jump straight to this session's GPS route */}
        {session.id && (
          <div className="flex justify-end gap-2 mb-3 pb-0">
            {onEdit && (
              <button
                onClick={() => onEdit(session)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-brand-200 bg-brand-50 text-brand-700 hover:bg-brand-100">
                <Edit2 size={12}/> {t('editSession.title')}
              </button>
            )}
            {!isSchool && (
              <button
                onClick={() => { onClose(); navigate(`/dashboard/booking/tracking?booking_session_id=${session.id}`) }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
                <Navigation size={12}/> {t('bookingDetails.trackingHistory')}
              </button>
            )}
          </div>
        )}

        {/* ── Basic Information ── */}
        <div className="rounded-xl overflow-hidden border border-gray-100 mb-4">
          <SessionSection
            icon={<BookOpen size={15} className="text-gray-600"/>}
            label={t('sessionDetail.basicInformation')}
          />
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            {/* Left column */}
            <div className="px-4">
              <SessionInfoCell
                label={t('sessionDetail.sessionId')}
                value={session.id}
                mono
              />
              <SessionInfoCell
                label={t('sessionDetail.sessionType')}
                value={session.session_type ?? t('bookingDetails.normal')}
              />
              {session.session_type === 'RESCHEDULED' && (
                <SessionInfoCell
                  label={t('sessionDetail.rescheduleReason')}
                  value={session.reschedule_reason || t('bookingDetails.noReasonProvided')}
                />
              )}
              <SessionInfoCell
                label={t('sessionDetail.sessionNo')}
                value={sessionNoDisplay}
              />
            </div>
            {/* Right column */}
            <div className="px-4">
              <SessionInfoCell
                label={t('sessionDetail.bookingId')}
                value={session.booking_id ?? bookingId}
                mono
              />
              <SessionInfoCell
                label={t('sessionDetail.sessionStatus')}
                value={
                  <span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold', sessionStatusCls(session.session_status ?? 'PENDING'))}>
                    {statusLabel(t, session.session_status) ?? '—'}
                  </span>
                }
              />
              <SessionInfoCell
                label={t('sessionDetail.totalSession')}
                value={session.no_of_sessions ?? '—'}
              />
            </div>
          </div>
        </div>

        {/* ── Time Line (Session Progress) ── */}
        <div className="rounded-xl overflow-hidden border border-gray-100">
          <SessionSection
            icon={<TrendingUp size={15} className="text-gray-600"/>}
            label={t('sessionDetail.timeline')}
          />
          <div className="px-5 py-4">
            {hasExplicitTimeline ? (
              /* ── Explicit timeline array from API (legacy shape) ── */
              explicitTimeline.map((item: any, i: number) => {
                const isPaused = (item.label ?? item.event ?? '').toLowerCase().includes('pause')
                const frozenAt = item.frozen_at ?? item.timer_frozen_at ?? null
                const pauseDuration = item.duration ?? item.pause_duration ?? null
                const pauseNote = item.note ?? item.reason ?? null
                const timeStr = fmtDT(item.time ?? item.created_at)
                return (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center pt-1">
                      <div className="w-3 h-3 rounded-full bg-brand-400 border-2 border-brand-200 flex-shrink-0"/>
                      {i < explicitTimeline.length - 1 && <div className="w-0.5 bg-gray-200 flex-1 mt-1 min-h-[20px]"/>}
                    </div>
                    <div className="flex-1 pb-4 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900">{item.label ?? item.event ?? '—'}</div>
                          <div className="text-[13px] text-gray-400">{t('sessionDetail.doneBy')}: {item.done_by ?? item.actor ?? '—'}</div>
                          {isPaused && frozenAt && (
                            <div className="text-[13px] text-gray-500 mt-1">({t('sessionDetail.timerFrozenAt')} <strong>{frozenAt}</strong>{pauseDuration && ` ${pauseDuration}`})</div>
                          )}
                          {pauseNote && (
                            <div className="mt-1.5 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-50 border border-brand-200 text-[13px] text-brand-700 font-semibold">
                              <AlertCircle size={11} className="flex-shrink-0"/>{pauseNote}
                            </div>
                          )}
                        </div>
                        {timeStr !== '—' && <div className="text-[13px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeStr}</div>}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              /* ── Built from API timestamp fields (real response shape) ── */
              STEPS.map((step, i) => {
                const isDone    = step.isDone
                const isCurrent = i === activeIdx && !isDone
                const timeStr   = step.time ? fmtDT(step.time) : null

                const dotCls = isDone
                  ? 'bg-green-500 border-green-400'
                  : isCurrent
                    ? 'bg-brand-500 border-brand-300'
                    : 'bg-gray-200 border-gray-300'

                const lineCls = isDone ? 'bg-green-400' : 'bg-gray-200'

                return (
                  <div key={step.key} className="flex gap-4">
                    {/* Dot + connector line */}
                    <div className="flex flex-col items-center">
                      <div className={cn(
                        'w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                        dotCls
                      )}>
                        {isDone
                          ? <CheckCircle size={16} className="text-white"/>
                          : isCurrent
                            ? <div className="w-2.5 h-2.5 rounded-full bg-white"/>
                            : <div className="w-2.5 h-2.5 rounded-full bg-gray-400"/>
                        }
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={cn('w-0.5 flex-1 mt-1 min-h-[32px]', lineCls)}/>
                      )}
                    </div>

                    {/* Step content */}
                    <div className="flex-1 pb-5 min-w-0 pt-1">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn('text-sm font-bold leading-tight', isDone || isCurrent ? 'text-gray-900' : 'text-gray-400')}>
                              {step.label}
                            </span>
                          </div>
                          <div className={cn('text-[13px] mt-0.5', isDone || isCurrent ? 'text-gray-500' : 'text-gray-300')}>
                            {step.desc}
                          </div>
                          {(isDone || isCurrent) && step.doneBy && (
                            <div className="text-[12px] text-gray-400 mt-0.5">{t('sessionDetail.doneBy')}: {step.doneBy}</div>
                          )}
                        </div>
                        {timeStr && isDone && (
                          <div className="text-[13px] text-gray-400 flex-shrink-0 whitespace-nowrap">{timeStr}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — Booking Details
// ══════════════════════════════════════════════════════════════════════════════
function BookingDetailsTab({ booking, onChangeTrainer }: { booking: any; onChangeTrainer?: () => void }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const navigate = useNavigate()
  const location = useLocation()
  const isSchool = location.pathname.startsWith('/school')
  const user   = booking.user   ?? {}
  const driver = booking.driver ?? {}
  // Trainer opens their profile in whichever panel we're in. Students only have a
  // details page on the super-admin side, so the card isn't a link for schools.
  const openTrainer = driver.id
    ? () => navigate(isSchool ? `/school/trainer/${driver.id}` : `/dashboard/user/${driver.id}`)
    : undefined
  const openStudent = user.id && !isSchool
    ? () => navigate(`/dashboard/user/${user.id}`)
    : undefined
  const pkg    = booking.package ?? {}
  const pickup = booking.pickup_location ?? null
  const [copied, setCopied] = useState(false)
  const userName   = localized(user, 'name', lang)
  const driverName = localized(driver, 'name', lang)

  const pkgName = resolveName(pkg, lang, 'package_translations', pkg?.name ?? '—')

  const pickupLabel = pickup?.label ?? pickup?.building_name ?? pickup?.address
    ?? [pickup?.plot_number, pickup?.city].filter(Boolean).join(', ') ?? null

  const totalSessions = pkg?.number_of_sessions ?? booking.total_sessions ?? 0
  // Unlimited packages come back either as an explicit is_unlimited flag or as
  // number_of_sessions/-1 with no flag at all — checking both so "-1" never
  // shows up literally where the ∞ symbol should be.
  const isUnlimited   = pkg?.is_unlimited === true || pkg?.is_unlimited === 1 || Number(totalSessions) < 0
  const sessionBooked = booking.no_of_sessions ?? booking.sessions?.length ?? 0
  const sessionCompleted = booking.no_of_sessions_completed ?? 0
  const sessionRemaining = isUnlimited ? '∞' : Math.max(0, totalSessions - sessionCompleted)

  const handleCopyMap = () => {
    if (pickup?.latitude && pickup?.longitude) {
      navigator.clipboard.writeText(`https://www.google.com/maps?q=${pickup.latitude},${pickup.longitude}`)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* ── Booking source note — only present on assistant/external bookings ── */}
      {booking.remarks && (
        <div className="lg:col-span-2 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5"/>
          <div className="min-w-0">
            <div className="text-xs font-bold text-amber-700 uppercase tracking-wide">{t('bookingDetails.bookedFrom')}</div>
            <div className="text-sm text-amber-800 mt-0.5">{booking.remarks}</div>
          </div>
        </div>
      )}

      {/* ── Student Info ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-brand-500 flex items-center gap-2">
          <Users size={14} className="text-white"/>
          <span className="text-sm font-bold text-white">{t('bookingDetails.studentInfo')}</span>
        </div>
        <div className="p-5 flex gap-4">
          <img src={user.photo_url || DUMMY_AVATAR} alt={userName} onError={onAvatarError}
            onClick={openStudent}
            className={cn('w-20 h-20 rounded-xl object-cover border border-gray-200 flex-shrink-0 bg-gray-100',
              openStudent && 'cursor-pointer hover:opacity-90 transition-opacity')}/>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {openStudent
                ? <button onClick={openStudent} className="text-base font-bold text-gray-900 hover:text-brand-500 hover:underline text-start">{userName}</button>
                : <h3 className="text-base font-bold text-gray-900">{userName}</h3>}
              <span className={cn('px-2 py-0.5 rounded-full text-[12px] font-bold', user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600')}>
                {user.is_active ? t('common.active') : t('common.inactive')}
              </span>
            </div>
            <div className="text-xs text-gray-400">{t('bookingDetails.studentIdPrefix')}{user.id?.toString().padStart(4,'0')}</div>
            {[
              { label: t('bookingDetails.phoneNumber'), value: user.phone ? `+${user.country_code??''} ${user.phone}` : '—' },
              { label: t('bookingDetails.email'),        value: user.email },
              { label: t('bookingDetails.dateOfBirth'),value: fmtDate(user.dob) },
              { label: t('bookingDetails.city'),         value: resolveName(user.city, lang, 'city_translations') },
              { label: t('bookingDetails.fileNumber'),  value: user.file_number ?? user.licence_number },
            ].map(({label, value}) => (
              <div key={label} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="text-gray-400 w-24 flex-shrink-0">{label}:</span>
                <span className="font-medium truncate">{value ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Trainer Info ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-teal-500 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-white"/>
            <span className="text-sm font-bold text-white">{t('bookingDetails.trainerInfo')}</span>
          </div>
          <button
            onClick={onChangeTrainer}
            className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[11px] font-semibold transition-colors">
            {t('bookingDetails.changeTrainer')}
          </button>
        </div>
        <div className="p-5 flex gap-4">
          <img src={driver.photo_url || DUMMY_AVATAR} alt={driverName} onError={onAvatarError}
            onClick={openTrainer}
            className={cn('w-20 h-20 rounded-xl object-cover border border-gray-200 flex-shrink-0 bg-gray-100',
              openTrainer && 'cursor-pointer hover:opacity-90 transition-opacity')}/>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {openTrainer
                ? <button onClick={openTrainer} className="text-base font-bold text-gray-900 hover:text-teal-600 hover:underline text-start">{driverName}</button>
                : <h3 className="text-base font-bold text-gray-900">{driverName}</h3>}
              <span className={cn('px-2 py-0.5 rounded-full text-[12px] font-bold', driver.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600')}>
                {driver.is_active ? t('common.active') : t('common.inactive')}
              </span>
            </div>
            <div className="text-xs text-gray-400">{t('bookingDetails.trainerIdPrefix')}{driver.id?.toString().padStart(4,'0')}</div>
            {[
              { label: t('bookingDetails.arabicName'), value: driver.name_ar },
              { label: t('bookingDetails.urduName'),   value: driver.name_ur },
              { label: t('bookingDetails.dob'),         value: fmtDate(driver.dob) },
              { label: t('bookingDetails.gender'),      value: driver.gender },
              { label: t('bookingDetails.email'),       value: driver.email },
              { label: t('bookingDetails.phone'),       value: driver.phone ? `+${driver.country_code??''} ${driver.phone}` : '—' },
              { label: t('bookingDetails.nationality'), value: driver.nationality },
            ].map(({label, value}) => (
              <div key={label} className="flex items-center gap-2 text-xs text-gray-600">
                <span className="text-gray-400 w-24 flex-shrink-0">{label}:</span>
                <span className="font-medium truncate">{value ?? '—'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Package & Vehicle Info ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-green-500 flex items-center gap-2">
          <Package size={14} className="text-white"/>
          <span className="text-sm font-bold text-white">{t('bookingDetails.packageVehicleInfo')}</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <MapPin size={13} className="text-gray-400"/>
              <div>
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('bookingDetails.city')}</div>
                <div className="text-xs font-bold text-gray-800">
                  {resolveName(booking.city, lang, 'city_translations')}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Car size={13} className="text-gray-400"/>
              <div>
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('bookingDetails.vehicle')}</div>
                <div className="text-xs font-bold text-gray-800">{booking.vehicle_type ?? booking.gear_type ?? '—'}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Settings size={13} className="text-gray-400"/>
              <div>
                <div className="text-[11px] text-gray-400 uppercase font-semibold">{t('bookingDetails.gear')}</div>
                <div className="text-xs font-bold text-gray-800">{booking.gear_type ?? '—'}</div>
              </div>
            </div>
          </div>

          {/* Package chip */}
          <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-brand-50 border border-brand-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-brand-200 flex items-center justify-center flex-shrink-0">
                <Package size={13} className="text-brand-700"/>
              </div>
              <span className="text-sm font-bold text-gray-800 truncate">{pkgName}</span>
            </div>
            <span className="text-sm font-black text-brand-600 flex-shrink-0">
              AED {Number(booking.total ?? booking.sub_total ?? 0).toLocaleString()}
            </span>
          </div>

          {/* Session stats */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: t('bookingDetails.totalSession'),    value: isUnlimited ? <InfinityIcon size={20}/> : totalSessions,   bg:'bg-gray-50' },
              { label: t('bookingDetails.sessionBooked'),   value: sessionBooked,    bg:'bg-brand-50'  },
              { label: t('bookingDetails.sessionCompleted'),value: sessionCompleted, bg:'bg-green-50' },
              { label: t('bookingDetails.remainingSession'),value: sessionRemaining, bg:'bg-brand-50' },
            ].map(({label, value, bg}) => (
              <div key={label} className={cn('rounded-xl p-2 text-center', bg)}>
                <div className="text-base font-black text-gray-800 flex items-center justify-center">{value}</div>
                <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Pickup Location ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-brand-500 flex items-center gap-2">
          <MapPin size={14} className="text-white"/>
          <span className="text-sm font-bold text-white">{t('bookingDetails.pickupLocation')}</span>
        </div>
        <div className="p-5">
          {!pickup ? (
            <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-brand-200 rounded-xl">
              <MapPin size={32} className="text-brand-200 mb-2"/>
              <p className="text-sm font-semibold text-gray-400">{t('bookingDetails.noPickupOption')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-[11px] text-gray-400 font-semibold uppercase flex items-center justify-between">
                <span>{t('bookingDetails.locationName')}</span>
                <span>{t('bookingDetails.distanceTrainerStudent')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-gray-800">{pickupLabel ?? '—'}</span>
                {booking.pickup_location_km != null && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <MapPin size={10}/>{booking.pickup_location_km} KM
                  </span>
                )}
              </div>
              {pickup.address && (
                <div>
                  <div className="text-[11px] text-gray-400 font-semibold uppercase mb-1">{t('bookingDetails.fullAddress')}</div>
                  <div className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-700">{pickup.address}</div>
                </div>
              )}
              {pickup.latitude && pickup.longitude && (
                <button onClick={handleCopyMap}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-brand-200 text-brand-600 text-xs font-semibold hover:bg-brand-50 transition-colors">
                  {copied ? <Check size={12}/> : <Copy size={12}/>}
                  {copied ? t('bookingDetails.copied') : t('bookingDetails.copyMapLink')}
                </button>
              )}
              {pickup.phone && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Phone size={11} className="text-gray-400"/>
                  <span>{pickup.phone}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — Payment & Sessions
// ══════════════════════════════════════════════════════════════════════════════
function PaymentSessionTab({ booking, onSessionClick, onSessionEdit }: {
  booking: any; onSessionClick: (s: any) => void; onSessionEdit: (s: any) => void
}) {
  const { t } = useTranslation()
  const paymentStatus = booking.payment_status ?? ''
  const paymentMethod = booking.payment_method ?? '—'
  const cardDetails   = booking.payment_method_details ?? null

  const fees = [
    { label: t('bookingDetails.serviceCharge'),   value: booking.tax_amount },
    { label: t('bookingDetails.transportFee'),    value: booking.transport_fee },
    { label: t('bookingDetails.cashServiceFee'), value: booking.cash_service_fee },
    { label: t('bookingDetails.discount'),         value: booking.discount },
  ]
  const extras = [
    { label: t('bookingDetails.amountDue'),       value: booking.amount_due },
    { label: t('bookingDetails.amountRefunded'),  value: booking.amount_refunded },
    ...(parseFloat(booking.wallet_amount_used ?? '0') !== 0
      ? [{ label: t('bookingDetails.walletAmountUsed'), value: booking.wallet_amount_used }] : []),
    ...(booking.coupon_code ? [{ label: t('bookingDetails.couponUsed'), value: booking.coupon_code, isText: true }] : []),
  ]
  const sessions: any[] = booking.sessions ?? []
  const hasCancelledSession = sessions.some((s: any) => (s.session_status ?? '') === 'CANCELLED')
  const hasRescheduledSession = sessions.some((s: any) => (s.session_type ?? '') === 'RESCHEDULED')

  const pStatCls = {
    PAID:    'bg-green-500 text-white',
    PENDING: 'bg-brand-500 text-white',
    FAILED:  'bg-red-500 text-white',
    REFUNDED:'bg-purple-500 text-white',
  }[(paymentStatus?.toUpperCase() as string)] ?? 'bg-gray-200 text-gray-600'

  return (
    <div className="space-y-5">
      {/* ── Payment summary tiles ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100">
          <span className="text-sm font-bold text-gray-800">{t('bookingDetails.paymentInformation')}</span>
          <div className="flex items-center gap-2">
            <span className={cn('px-3 py-1 rounded-lg text-xs font-bold flex items-center gap-1', pStatCls)}>
              {statusLabel(t, paymentStatus)} <ChevronDown size={11}/>
            </span>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: t('bookingDetails.paymentMethod'), value: paymentMethod,     bg:'bg-teal-500',  icon:<CreditCard size={20} className="text-white"/> },
              { label: t('bookingDetails.packagePrice'),  value: `AED ${Number(booking.sub_total??0).toFixed(2)}`, bg:'bg-brand-500', icon:<Package size={20} className="text-white"/> },
              { label: t('bookingDetails.amountPaid'),    value: `AED ${Number(booking.amount_paid??0).toFixed(2)}`, bg:'bg-green-500', icon:<CheckCircle size={20} className="text-white"/> },
              { label: t('bookingDetails.totalAmount'),   value: `AED ${Number(booking.total??0).toFixed(2)}`, bg:'bg-purple-500', icon:<Dirham size={20} className="text-white"/> },
            ].map(({label, value, bg, icon}) => (
              <div key={label} className={cn('rounded-xl p-4 text-white', bg)}>
                <div className="mb-2">{icon}</div>
                <div className="text-[11px] font-semibold uppercase opacity-80">{label}</div>
                <div className="text-lg font-black mt-0.5">{value}</div>
              </div>
            ))}
          </div>

          {/* Fee breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              {fees.map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">AED {Number(value ?? 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              {extras.map(({ label, value, isText }) => (
                <div key={label} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                  <span className="text-xs text-gray-500">{label}</span>
                  <span className="text-xs font-semibold text-gray-800">
                    {isText ? value : `AED ${Number(value ?? 0).toFixed(2)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          {(booking.payment_id || booking.total_pickup_fee != null) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs font-bold text-gray-700 mb-2">{t('bookingDetails.additionalInformation')}</div>
              <div className="grid grid-cols-2 gap-3">
                {booking.payment_id && (
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                    <span className="text-xs text-gray-500">{t('bookingDetails.paymentId')}</span>
                    <span className="text-xs font-mono font-semibold">{booking.payment_id}</span>
                  </div>
                )}
                {booking.total_pickup_fee != null && (
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                    <span className="text-xs text-gray-500">{t('bookingDetails.totalPickupFee')}</span>
                    <span className="text-xs font-semibold">AED {Number(booking.total_pickup_fee).toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Card details */}
          {cardDetails && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="text-xs font-bold text-gray-700 mb-2">{t('bookingDetails.paymentMethodDetails')}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: t('bookingDetails.cardNo'),     value: cardDetails.card_number ?? cardDetails.last4 ? `**** **** **** ${cardDetails.last4}` : null },
                  { label: t('bookingDetails.cardHolder'), value: cardDetails.card_holder ?? cardDetails.billing_address },
                  { label: t('bookingDetails.expiryDate'), value: cardDetails.expiry_date ?? cardDetails.expiry },
                  { label: t('bookingDetails.gateway'),     value: cardDetails.gateway ?? cardDetails.brand },
                  { label: t('bookingDetails.repeatCard'), value: cardDetails.is_repeat ? t('common.yes') : cardDetails.is_repeat === false ? t('common.no') : null },
                ].filter(i=>i.value).map(({label, value}) => (
                  <div key={label} className="px-3 py-2 rounded-lg bg-gray-50">
                    <div className="text-[11px] text-gray-400 uppercase font-semibold">{label}</div>
                    <div className="text-xs font-semibold text-gray-800 mt-0.5">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Booking Summary (sessions) ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-800">{t('bookingDetails.bookingSummary')}</span>
        </div>
        {sessions.length === 0 ? (
          <div className="text-sm text-gray-400 text-center py-8">{t('bookingDetails.noSessionsFound')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th>{t('bookingDetails.colSession')}</th>
                  <th>{t('bookingDetails.colDate')}</th>
                  <th>{t('bookingDetails.colTime')}</th>
                  <th>{t('bookingDetails.colType')}</th>
                  <th>{t('bookingDetails.colStatus')}</th>
                  {hasRescheduledSession && <th>{t('bookingDetails.colRescheduleReason')}</th>}
                  {hasCancelledSession && <th>{t('bookingDetails.colCancellation')}</th>}
                  <th className="text-end">{t('bookingDetails.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s: any, i: number) => {
                  const sStatus = s.session_status ?? 'PENDING'
                  const sCls = sessionStatusCls(sStatus)
                  const isCancelled = sStatus === 'CANCELLED'
                  // A cancelled session carries an impression from whichever side
                  // cancelled it — the trainer's driver_impression, or the student's
                  // user_impression. Trainer takes priority since a trainer-side
                  // cancellation can still leave a stale user_impression behind.
                  const driverImpression = s.driver_impression
                  const userImpression = s.user_impression
                  const sessionLabel = Array.isArray(s.session_no)
                    ? s.session_no.map((n:number)=>`${n}${['st','nd','rd'][n-1]||'th'}`).join(' & ')
                    : `${s.session_no ?? i+1}${['st','nd','rd'][(s.session_no??i+1)-1]||'th'}`
                  return (
                    <tr key={s.id ?? i} className="cursor-pointer" onClick={() => onSessionClick(s)}>
                      <td className="font-semibold text-gray-800 whitespace-nowrap">{sessionLabel}</td>
                      <td className="text-gray-600">
                        <span className="flex items-center gap-1"><Calendar size={11} className="text-gray-400"/>{fmtDate(s.start_time)}</span>
                      </td>
                      <td className="text-gray-600 whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Clock size={11} className="text-gray-400"/>
                          {s.start_time ? new Date(s.start_time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—'}
                          {' – '}
                          {s.end_time ? new Date(s.end_time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',hour12:true}) : ''}
                        </span>
                      </td>
                      <td className="text-gray-600">{s.session_type ?? t('bookingDetails.normal')}</td>
                      <td><span className={cn('px-2.5 py-0.5 rounded-full text-[12px] font-bold whitespace-nowrap', sCls)}>{statusLabel(t, sStatus)}</span></td>
                      {hasRescheduledSession && (
                        <td className="max-w-[220px]">
                          {s.session_type === 'RESCHEDULED' ? (
                            <span className="text-[12px] text-gray-500 italic truncate block" title={s.reschedule_reason || undefined}>
                              {s.reschedule_reason || t('bookingDetails.noReasonProvided')}
                            </span>
                          ) : <span className="text-xs text-gray-300">—</span>}
                        </td>
                      )}
                      {hasCancelledSession && (
                        <td className="max-w-[240px]">
                          {isCancelled ? (
                            driverImpression ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                                  <Car size={10}/>{t('bookingDetails.cancelledByTrainer')}
                                </span>
                                <span className="text-[12px] text-gray-500 italic truncate block" title={driverImpression}>{driverImpression}</span>
                              </div>
                            ) : userImpression ? (
                              <div className="flex flex-col gap-0.5">
                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                                  <Users size={10}/>{t('bookingDetails.cancelledByStudent')}
                                </span>
                                <span className="text-[12px] text-gray-500 italic truncate block" title={userImpression}>{userImpression}</span>
                              </div>
                            ) : (
                              <span className="text-[12px] text-gray-400 flex items-center gap-1"><AlertCircle size={10}/>{t('bookingDetails.noFeedback')}</span>
                            )
                          ) : <span className="text-xs text-gray-300">—</span>}
                        </td>
                      )}
                      <td className="text-end">
                        {/* Row click opens the read-only detail modal, so the edit
                            action has to stop propagation to win the click. */}
                        <button
                          onClick={(e) => { e.stopPropagation(); onSessionEdit(s) }}
                          title={t('editSession.title')}
                          aria-label={t('editSession.title')}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 bg-white hover:bg-brand-50 hover:text-brand-600 hover:border-brand-200 transition-colors"
                        >
                          <Edit2 size={11}/>{t('common.edit')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { value: 'details',  labelKey: 'bookingDetails.tabDetails'   },
  { value: 'payment',  labelKey: 'bookingDetails.tabPayment' },
]

export function BookingDetailsPage() {
  const { t }     = useTranslation()
  const { id }    = useParams<{ id: string }>()
  const navigate  = useNavigate()
  const location  = useLocation()
  const isSchool  = location.pathname.startsWith('/school')
  const qc        = useQueryClient()
  const [tab, setTab] = useState('details')
  const [selectedSession, setSelectedSession] = useState<any | null>(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [changeTrainerOpen, setChangeTrainerOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<any | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['booking-detail', id],
    queryFn: () => bookingService.getById(Number(id)),
    enabled: !!id,
  })

  // Booking status enum
  const { data: statusEnum } = useQuery({
    queryKey: ['booking-status-enum'],
    queryFn: () => restClient.get(endpoints.booking.getBookingStatus).then(r => r.data),
    staleTime: Infinity,
  })

  const updateStatus = useMutation({
    mutationFn: (fd: FormData) => bookingService.updateStatus(fd as any),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['booking-detail', id] }); setStatusOpen(false) },
  })

  const booking = (data as any)?.data ?? data ?? {}
  const bookingStatus = booking.booking_status ?? booking.status ?? '—'
  const paymentStatus = booking.payment_status ?? '—'
  // Real shape (per old dashboard's enum hooks): { status, message, values: [...] } —
  // NOT `.data`. Without this the dropdown silently rendered zero options, making
  // status change look broken (button opened, but nothing was clickable).
  const statusOptions: any[] = Array.isArray((statusEnum as any)?.values) ? (statusEnum as any).values
    : Array.isArray((statusEnum as any)?.data) ? (statusEnum as any).data
    : Array.isArray(statusEnum) ? statusEnum : []

  if (isLoading) return (
    <div className="space-y-5 animate-pulse">
      <Sk className="h-8 w-48"/><Sk className="h-12 w-full"/>
      <div className="grid grid-cols-2 gap-5"><Sk className="h-64"/><Sk className="h-64"/></div>
    </div>
  )
  if (isError) return (
    <div className="flex flex-col items-center py-20 text-center">
      <p className="text-sm font-semibold text-red-500">{t('bookingDetails.failedToLoad')}</p>
      <button onClick={() => navigate(-1)} className="mt-3 text-xs text-brand-600 hover:underline flex items-center gap-1">
        <ChevronLeft size={12}/>{t('bookingDetails.goBack')}
      </button>
    </div>
  )

  return (
    <div className="space-y-5">

      {/* ── Breadcrumb + Title ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
            <span className="cursor-pointer hover:text-brand-500" onClick={() => navigate(isSchool ? '/school/booking/list' : '/dashboard/booking/list')}>{t('bookingDetails.breadcrumbDashboard')}</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-brand-500" onClick={() => navigate(isSchool ? '/school/booking/list' : '/dashboard/booking/list')}>{t('bookingDetails.breadcrumbBookings')}</span>
            <span>/</span>
            <span className="text-brand-500 font-semibold">{t('bookingDetails.title')}</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{t('bookingDetails.title')}</h1>
        </div>
        {/* Status badge + edit */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* GPS tracking history for every session of this booking */}
          {!isSchool && (
            <button
              onClick={() => navigate(`/dashboard/booking/tracking?booking_id=${booking.id ?? id}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
              <Navigation size={12}/> {t('bookingDetails.trackingHistory')}
            </button>
          )}
          <div className="relative">
            <button
              onClick={() => setStatusOpen(o => !o)}
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border',
                bookingStatus === 'CONFIRMED' ? 'bg-green-500 text-white border-green-500'
                : bookingStatus === 'CANCELLED' ? 'bg-red-500 text-white border-red-500'
                : bookingStatus === 'COMPLETED' ? 'bg-green-500 text-white border-green-500'
                : 'bg-brand-500 text-white border-brand-500')}>
              {tab === 'details' ? t('bookingDetails.bookingStatusLabel') : t('bookingDetails.paymentStatusLabel')}
              {statusLabel(t, tab === 'details' ? bookingStatus : paymentStatus)}
              <ChevronDown size={11}/>
            </button>
            {statusOpen && (
              <div className="absolute end-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-10 min-w-36 py-1">
                {statusOptions.map((opt: any) => (
                  <button key={opt.id ?? opt.value} onClick={() => {
                    const fd = new FormData()
                    fd.append('booking_id', String(id))
                    fd.append('booking_status', String(opt.id ?? opt.value))
                    updateStatus.mutate(fd)
                  }}
                    className="w-full px-4 py-2 text-start text-xs hover:bg-brand-50 hover:text-brand-600 transition-colors">
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-1 flex items-center gap-1">
        {TABS.map(tb => (
          <button key={tb.value} onClick={() => setTab(tb.value)}
            className={cn('px-4 py-2.5 text-xs font-semibold rounded-xl whitespace-nowrap transition-colors',
              tab === tb.value ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-500 hover:text-brand-600 hover:bg-brand-50')}>
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      {tab === 'details'  && (
        <BookingDetailsTab
          booking={booking}
          onChangeTrainer={() => setChangeTrainerOpen(true)}
        />
      )}
      {tab === 'payment'  && (
        <PaymentSessionTab
          booking={booking}
          onSessionClick={setSelectedSession}
          onSessionEdit={(s) => { setSelectedSession(null); setEditingSession(s) }}
        />
      )}

      {/* ── Session Detail Modal ── */}
      <SessionDetailModal
        open={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        session={selectedSession}
        bookingId={booking.id}
        onEdit={(s) => { setSelectedSession(null); setEditingSession(s) }}
      />

      {/* ── Edit Session Modal ── */}
      <EditSessionModal
        open={!!editingSession}
        onClose={() => setEditingSession(null)}
        session={editingSession}
        bookingId={id}
      />

      {/* ── Change Trainer Modal ── */}
      <ChangeTrainerModal
        open={changeTrainerOpen}
        onClose={() => setChangeTrainerOpen(false)}
        booking={booking}
      />
    </div>
  )
}
